import { Link } from "react-router-dom";
import { Phone, Target, Users, Heart, Award, ArrowRight } from "lucide-react";
import { useSEO } from "@/hooks/useSEO";
import { PageHeader } from "@/components/PageHeader";
import { Reveal } from "@/components/Reveal";
import { COMPANY, IMG } from "@/lib/constants";

const VALUES = [
  { icon: Target, title: "Our Mission", desc: "To provide the highest quality skilled care wherever care is needed — rehab-quality services in the comfort of your home." },
  { icon: Users, title: "Team Approach", desc: "Our team approach and geriatric care expertise allow us to design the very best plan for you and your family." },
  { icon: Heart, title: "Compassion First", desc: "Professional and compassionate clinical staff who make every patient feel safe, secure, and truly cared for." },
  { icon: Award, title: "Care Experts", desc: "A preferred provider for most major insurance plans. If we can't take it, we'll help place it." },
];

export default function About() {
  useSEO({
    title: "About Us | Esprit Home Healthcare — Northern Virginia",
    description: "Learn about Esprit Home Healthcare's mission to deliver the highest quality skilled care at home, with geriatric expertise and a compassionate team approach.",
  });

  return (
    <div>
      <PageHeader
        overline="About Us"
        title="The Care Experts of Northern Virginia"
        subtitle="With modern healthcare equipment and skilled home healthcare practitioners, you can receive rehab-quality services in the comfort of your home."
      />

      <section className="container-page py-16 lg:py-24 grid lg:grid-cols-2 gap-12 items-center">
        <Reveal>
          <img src={IMG.about} alt="Compassionate home healthcare team" className="rounded-[2rem] w-full h-[460px] object-cover shadow-lg" />
        </Reveal>
        <Reveal delay={0.1}>
          <h2 className="font-heading text-4xl md:text-5xl text-primary">Welcome to Esprit Home Healthcare</h2>
          <p className="text-lg text-foreground/80 mt-5 leading-relaxed">
            Choosing a homecare provider for you or your loved one can be a difficult task. It's important to choose a recognized provider like Esprit Home Healthcare.
          </p>
          <p className="text-lg text-foreground/80 mt-4 leading-relaxed">
            Getting services from Esprit is easy. One quick call to our friendly staff will assist you in getting Skilled Nursing, Therapy, Medical Social Worker services, and Personal Care including Private Duty services. We are a preferred provider for most major insurance plans — and if we can't take it, we'll help place it.
          </p>
          <a href={COMPANY.phoneHref} className="inline-flex items-center gap-2 rounded-full bg-primary hover:bg-primary/90 text-primary-foreground h-14 px-7 text-lg font-bold mt-7 transition-colors">
            <Phone className="w-5 h-5" /> Call {COMPANY.phone}
          </a>
        </Reveal>
      </section>

      <section className="bg-[hsl(var(--muted))]">
        <div className="container-page py-20">
          <Reveal className="text-center max-w-2xl mx-auto">
            <h2 className="font-heading text-4xl md:text-5xl text-primary">What We Stand For</h2>
          </Reveal>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6 mt-12">
            {VALUES.map((v, i) => (
              <Reveal key={v.title} delay={i * 0.08}>
                <div className="h-full bg-card rounded-2xl border border-border p-7">
                  <span className="grid place-items-center w-14 h-14 rounded-xl bg-secondary/15 text-secondary">
                    <v.icon className="w-7 h-7" />
                  </span>
                  <h3 className="font-heading text-2xl text-primary mt-5">{v.title}</h3>
                  <p className="text-muted-foreground mt-2 leading-relaxed">{v.desc}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="container-page py-20 text-center">
        <Reveal>
          <h2 className="font-heading text-4xl md:text-5xl text-primary max-w-3xl mx-auto">Ready to design the right care plan for your family?</h2>
          <Link to="/contact" className="inline-flex items-center gap-2 rounded-full bg-accent hover:bg-accent/90 text-accent-foreground h-16 px-8 text-xl font-bold mt-8 transition-colors">
            Get Started Today <ArrowRight className="w-5 h-5" />
          </Link>
        </Reveal>
      </section>
    </div>
  );
}
